import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiQuery } from '../../api/hooks';
import type { CashSummaryResponse, SaleSummaryResponse, SalesSummaryResponse } from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { useChartsShown } from '../../components/charts/useChartsShown';
import { ErrorState, LoadingState } from '../../components/states/StateViews';
import { PageHeader } from '../../components/PageHeader';
import { PanelHeading } from './PanelHeading';
import { useAuthStore } from '../../auth/authStore';
import { useLocalisedName } from '../../i18n/localisedName';
import { SalesOverview } from '../insight/SalesOverview';
import { clinicToday, shiftDay } from '../scheduling/clinicTime';
import { useBranchPicker } from '../scheduling/useBranchPicker';
import { money } from '../catalog/money';
import { CloseDrawer } from './CloseDrawer';
import { useOpenReceipt } from './useOpenReceipt';

/** The periods the charts offer, in days, each ending today (CR-34). */
const PERIODS = [7, 30, 90, 365] as const;

/**
 * The day's takings at a branch (FR-901, FR-902, FR-908).
 *
 * Three questions, in the order the desk asks them: what was sold today, what is still owed, and
 * does the drawer balance. The cash summary sits at the top rather than the bottom because it is
 * the number the owner opens this screen for.
 *
 * Above them, for whoever may read the cash summary, the money over the last week, month, quarter
 * or year as charts (CR-34), for this branch or every branch. They fold away like the other
 * screens' charts.
 */
export function SalesPage() {
  const { t } = useTranslation();
  const { name } = useLocalisedName();
  const { clinicId, branchId, branches, choose } = useBranchPicker();
  const canClose = useAuthStore((state) => state.permissions).has('CASH_SUMMARY_READ');

  const [day, setDay] = useState(() => clinicToday());
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [chartsShown, showCharts] = useChartsShown('sales');
  const [period, setPeriod] = useState<(typeof PERIODS)[number]>(30);
  const [everyBranch, setEveryBranch] = useState(false);

  const query = outstandingOnly ? `${branchId}:outstanding` : `${branchId}:${day}`;
  const sales = useApiQuery<SaleSummaryResponse[]>(
    queryKeys.sales(clinicId ?? '', query),
    outstandingOnly
      ? `/clinics/${clinicId}/sales?branchId=${branchId}&outstanding=true`
      : `/clinics/${clinicId}/sales?branchId=${branchId}&day=${day}`,
    { enabled: Boolean(clinicId && branchId) },
  );

  const summary = useApiQuery<CashSummaryResponse>(
    queryKeys.cashSummary(clinicId ?? '', branchId ?? '', day),
    `/clinics/${clinicId}/cash-summary?branchId=${branchId}&day=${day}`,
    { enabled: Boolean(clinicId && branchId && canClose) },
  );

  const to = clinicToday();
  const from = shiftDay(to, -(period - 1));
  const overall = everyBranch && branches.length > 1;
  const filtered = `from=${from}&to=${to}${overall ? '' : `&branchId=${branchId}`}`;
  const overview = useApiQuery<SalesSummaryResponse>(
    queryKeys.salesSummary(clinicId ?? '', filtered),
    `/clinics/${clinicId}/sales/summary?${filtered}`,
    { enabled: Boolean(clinicId && (overall || branchId) && canClose && chartsShown), keepPrevious: true },
  );

  const receipt = useOpenReceipt(clinicId ?? '');

  if (!clinicId) {
    return <PageHeader title={t('nav.items.sales')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader
        title={t('nav.items.sales')}
        description={t('sales.description')}
        actions={
          canClose ? (
            <button type="button" className="button button--ghost" onClick={() => showCharts(!chartsShown)}>
              {chartsShown ? t('overview.hide') : t('overview.show')}
            </button>
          ) : null
        }
      />

      <div className="filters">
        <label className="field">
          <span>{t('board.branch')}</span>
          <select value={branchId ?? ''} onChange={(event) => choose(event.target.value)}>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {name(branch, branch.code)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('sales.day')}</span>
          <input type="date" value={day} onChange={(event) => setDay(event.target.value)} />
        </label>
        <label className="field checkbox">
          <input
            type="checkbox"
            checked={outstandingOnly}
            onChange={(event) => setOutstandingOnly(event.target.checked)}
          />
          <span>{t('sales.outstandingOnly')}</span>
        </label>
      </div>

      {canClose && chartsShown ? (
        <>
          <div className="toolbar overview-bar">
            <div className="tabs" role="tablist" aria-label={t('overview.sales.period')}>
              {PERIODS.map((days) => (
                <button
                  key={days}
                  type="button"
                  role="tab"
                  aria-selected={period === days}
                  className={period === days ? 'tab tab--active' : 'tab'}
                  onClick={() => setPeriod(days)}
                >
                  {t(`overview.sales.periods.${days}`)}
                </button>
              ))}
            </div>
            {branches.length > 1 ? (
              <div className="tabs" role="tablist" aria-label={t('overview.sales.scope')}>
                {([false, true] as const).map((every) => (
                  <button
                    key={String(every)}
                    type="button"
                    role="tab"
                    aria-selected={everyBranch === every}
                    className={everyBranch === every ? 'tab tab--active' : 'tab'}
                    onClick={() => setEveryBranch(every)}
                  >
                    {every ? t('overview.sales.allBranches') : t('overview.sales.thisBranch')}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          {overview.data ? (
            <SalesOverview
              summary={overview.data}
              from={from}
              to={to}
              branchName={(id) => {
                const branch = branches.find((candidate) => candidate.id === id);
                return branch ? name(branch, branch.code) : t('overview.noLongerListed');
              }}
              showBranches={overall}
            />
          ) : overview.isPending ? (
            <LoadingState rows={3} />
          ) : overview.error ? (
            <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
          ) : null}
        </>
      ) : null}

      {canClose ? (
        <AsyncSection query={summary}>
          {(day908) => (
            <section className="panel">
              <PanelHeading title={t('sales.cashSummary')} hint={t('sales.cashSummaryHint')} />
              <dl className="totals">
                <dt>{t('sales.expectedCash')}</dt>
                <dd>
                  <strong>{money(day908.expectedCash)}</strong>
                </dd>
                <dt>{t('sales.grossSold')}</dt>
                <dd>{money(day908.grossSold)}</dd>
                <dt>{t('sales.refunded')}</dt>
                <dd>{money(day908.refunded)}</dd>
                <dt>{t('sales.expensesPaid')}</dt>
                <dd>{money(day908.expensesPaid)}</dd>
                <dt>{t('sales.outstandingCash')}</dt>
                <dd>{money(day908.outstandingCash)}</dd>
              </dl>

              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('checkout.method')}</th>
                      <th className="numeric">{t('sales.count')}</th>
                      <th className="numeric">{t('sales.total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(day908.byMethod ?? []).map((row) => (
                      <tr key={row.method}>
                        <td>{t(`checkout.methods.${row.method}`)}</td>
                        <td className="numeric">{row.count}</td>
                        <td className="numeric">{money(row.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {(day908.counts ?? []).length > 0 ? (
                <ul className="counts">
                  {(day908.counts ?? []).map((count) => (
                    <li key={count.id}>
                      {count.countedByName} · {money(count.counted)} ·{' '}
                      <span className={count.variance?.amount === '0.00' ? 'ok' : 'warn'}>
                        {t('sales.variance', { amount: money(count.variance) })}
                      </span>
                      {count.note ? <span className="muted"> — {count.note}</span> : null}
                    </li>
                  ))}
                </ul>
              ) : null}

              <CloseDrawer clinicId={clinicId} branchId={branchId ?? ''} day={day} expected={day908.expectedCash} />
            </section>
          )}
        </AsyncSection>
      ) : null}

      <AsyncSection query={sales} skeletonRows={6}>
        {(rows) => (
          <section className="panel">
            <PanelHeading title={t('sales.list')} hint={t('sales.listHint')} />
            {rows.length === 0 ? (
              <p className="muted">{t('sales.none')}</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('sales.receipt')}</th>
                      <th>{t('checkout.patient')}</th>
                      <th>{t('sales.status')}</th>
                      <th>{t('checkout.total')}</th>
                      <th>{t('sales.paid')}</th>
                      <th>{t('checkout.outstanding')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((sale) => (
                      <tr key={sale.id}>
                        <td>{sale.receiptNumber}</td>
                        <td>{sale.patientName}</td>
                        <td>
                          <span className={`pill pill--${(sale.status ?? '').toLowerCase()}`}>
                            {t(`sales.statuses.${sale.status}`)}
                          </span>
                        </td>
                        <td>{money(sale.gross)}</td>
                        <td>{money(sale.paid)}</td>
                        <td>{money(sale.outstanding)}</td>
                        <td>
                          <button
                            type="button"
                            className="button button--ghost button--compact"
                            disabled={receipt.opening === sale.id}
                            aria-label={t('sales.receiptFor', { receipt: sale.receiptNumber ?? '' })}
                            onClick={() => void receipt.open(sale.id ?? '', sale.receiptNumber)}
                          >
                            {t('sales.receipt')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </AsyncSection>
      {receipt.error ? <ErrorState error={receipt.error} /> : null}
    </>
  );
}
